import { cn } from '@/lib/utils';

import { PrivateIcon } from '@/components/icons/PrivateIcon';
import { PublicIcon } from '@/components/icons/PublicIcon';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface StepPublishProps {
    visibility: 'private' | 'org';
    setVisibility: (v: 'private' | 'org') => void;
    agentName: string;
    selectedAgentName?: string;
    userLabel?: string;
    // Row-level security runs the job under the owner's own data access —
    // making it public would let others see output generated with that
    // owner's permissions, so "Public" is unavailable while it's on (see
    // StepContext's RLS toggle).
    rlsEnabled?: boolean;
}

const VISIBILITY_OPTIONS = [
    {
        value: 'private' as const,
        icon: PrivateIcon,
        title: 'Private',
        desc: 'Only you can see this job and its output.'
    },
    {
        value: 'org' as const,
        icon: PublicIcon,
        title: 'Public',
        desc: 'Visible to everyone in your organization.'
    }
];

export function StepPublish({
    visibility,
    setVisibility,
    agentName,
    selectedAgentName,
    userLabel,
    rlsEnabled
}: StepPublishProps) {
    return (
        <div className="space-y-6">
            <div className="">
                <h2 className="mb-0 font-medium text-2xl">Publish</h2>
                <p className="text-muted-foreground text-base">
                    Control who can see this agent and its output.
                </p>
            </div>

            <div className="space-y-4">
                <RadioGroup
                    value={visibility}
                    onValueChange={(v) => setVisibility(v as 'private' | 'org')}
                    className="gap-6 grid grid-cols-2"
                >
                    {VISIBILITY_OPTIONS.map((opt) => {
                        const isSelected = visibility === opt.value;
                        const disabled = rlsEnabled && opt.value === 'org';
                        const inputId = `publish-visibility-${opt.value}`;
                        return (
                            <label
                                key={opt.value}
                                htmlFor={inputId}
                                className={cn(
                                    'relative flex flex-col justify-center items-center gap-2 p-8 border rounded-lg text-center transition-all',
                                    disabled
                                        ? 'opacity-50 cursor-not-allowed border-border bg-muted/20'
                                        : 'cursor-pointer',
                                    !disabled && isSelected
                                        ? 'border-foreground bg-muted/40'
                                        : '',
                                    !disabled && !isSelected
                                        ? 'border-border bg-background hover:bg-accent/50'
                                        : ''
                                )}
                            >
                                <RadioGroupItem
                                    value={opt.value}
                                    id={inputId}
                                    disabled={disabled}
                                    className={cn(
                                        'top-3.5 left-3.5 absolute border size-5 [&_svg]:size-2.5',
                                        isSelected
                                            ? 'border-foreground'
                                            : 'border-border'
                                    )}
                                />

                                <opt.icon className="w-14 h-14" />

                                <div className="flex flex-col items-center gap-0.5 mt-4">
                                    <span className="font-medium text-base">
                                        {opt.title}
                                    </span>
                                    <p className="text-secondary-foreground text-sm text-center">
                                        {disabled
                                            ? 'Unavailable while row-level security is enabled — the job runs under your own data access.'
                                            : opt.desc}
                                    </p>
                                </div>
                            </label>
                        );
                    })}
                </RadioGroup>

                {/* <div className="flex items-center gap-2.5 bg-muted/30 p-4 border border-border rounded-[14px]">
                    <div
                        className="flex justify-center items-center rounded-[0.625rem] size-[38px] font-bold text-[13px] text-white shrink-0"
                        style={{ backgroundColor: '#2563eb' }}
                    >
                        {(agentName || '?')[0]}
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold text-[13.5px] truncate">
                            {agentName || 'Agent name'}
                        </div>
                        <div className="text-[12px] text-muted-foreground truncate">
                            {userLabel ?? 'You'}
                            {selectedAgentName
                                ? ` · runs on ${selectedAgentName}`
                                : ''}
                        </div>
                    </div>
                </div> */}
            </div>
        </div>
    );
}
